import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'contexts/AuthContext';
import AuthService from 'services/AuthService';
import AzureAuthService from 'services/AzureAuthService';
import GoogleAuthService from 'services/GoogleAuthService';

/**
 * Sign in again *over* the app instead of in place of it.
 *
 * The problem this solves is not authentication — that already worked. It is
 * that ending a session used to unmount the whole tree (`ProtectedRoute` swaps
 * in `LoginForm` the moment `isAuthenticated` goes false), so an expiry at the
 * wrong moment threw away a half-written CEL expression or an open HITL review
 * with no warning and nothing to save to. This dialog renders on top of a tree
 * that is still mounted; when it closes, the page is exactly as it was.
 *
 * Honest about what it can and cannot save:
 *  - **password sessions** are fully restored in place — `reauth()` re-mints
 *    the token without touching `isLoading`;
 *  - **SSO sessions** are restored in place only while the EasyAuth platform
 *    session is alive. Once that is gone, the only way back is a redirect to
 *    the provider, which unloads the document and *does* lose the work. The
 *    dialog says so rather than pretending otherwise.
 */
const ReauthDialog: React.FC = () => {
  const { t } = useTranslation('auth');
  const {
    user,
    sessionLapsed,
    reauthRequested,
    cancelReauth,
    reauth,
    renewSessionNow,
    logout,
    renewalRefusal,
  } = useAuth();

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoExhausted, setSsoExhausted] = useState(false);

  // Read from storage rather than from `user`: records written before the
  // provider was recorded have none, and those get the password form, which is
  // the safe default (a wrong SSO renewal can swap identity; a password prompt
  // cannot).
  const provider = AuthService.getSessionProvider() ?? user?.provider ?? 'password';
  const isSso = provider === 'azure' || provider === 'google';

  /**
   * The backend has ended this session on purpose — the absolute cap, an
   * administrator's revocation, a deactivated account. Retrying renewal would
   * ask the same question and get the same answer, so the prompt offers a real
   * sign-in instead of a button that cannot work.
   */
  const terminalRefusal =
    renewalRefusal === 'session_expired_absolute' ||
    renewalRefusal === 'revoked' ||
    renewalRefusal === 'user_inactive';

  const refusalMessage = renewalRefusal
    ? t(`session.refusal.${renewalRefusal}`, { defaultValue: t('session.refusal.generic') })
    : null;

  // Two ways in, with different stakes. A *lapse* is involuntary and there is
  // nothing usable behind the dialog, so it cannot be dismissed. A *requested*
  // re-auth is the user extending a still-valid session from the warning
  // banner, so it must be cancellable — the app behind it still works.
  const open = sessionLapsed || reauthRequested;
  if (!open || !user) return null;

  const handleReauth = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await reauth(password);
      setPassword('');
      cancelReauth();
    } catch (err: any) {
      // `reauth` has already ended the session in this case; the tree is gone
      // and `LoginForm` is showing, so this message is the last word about why.
      setError(err?.message === 'DIFFERENT_USER' ? t('session.differentUser') : t('session.reauthFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSsoRetry = async () => {
    setBusy(true);
    setError(null);
    try {
      const renewed = await renewSessionNow();
      if (!renewed) setSsoExhausted(true);
    } finally {
      setBusy(false);
    }
  };

  // Last resort for an SSO session whose platform cookie has also expired.
  // Navigates away, which is why it is never the first thing offered.
  const handleSsoRedirect = () => {
    if (provider === 'azure') AzureAuthService.initiateMicrosoftLogin();
    else GoogleAuthService.initiateGoogleLogin();
  };

  return (
    <Dialog
      open
      // No backdrop dismissal and no ESC: there is nothing useful behind it
      // until the session is restored. That makes the sign-out button below
      // mandatory — the header is unreachable while this is up.
      disableEscapeKeyDown={sessionLapsed}
      onClose={(_event, reason) => {
        if (sessionLapsed || reason === 'backdropClick') return;
        cancelReauth();
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="reauth-dialog-title"
    >
      <DialogTitle id="reauth-dialog-title">
        {sessionLapsed ? t('session.reauthTitle') : t('session.extendTitle')}
      </DialogTitle>

      <form onSubmit={handleReauth}>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {!sessionLapsed
              ? t('session.extendBody')
              : isSso
                ? t('session.reauthBodySso')
                : t('session.reauthBody')}
          </DialogContentText>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {user.username}
          </Typography>

          {refusalMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>{refusalMessage}</Alert>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {ssoExhausted && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('session.reauthSsoFailed')}</Alert>
          )}

          {!isSso && (
            <TextField
              autoFocus
              fullWidth
              type="password"
              label={t('password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="current-password"
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {/* With no ESC and no backdrop dismissal, a lapsed user who does not
              know the password would otherwise be trapped: the header — and its
              sign-out — is unreachable behind the modal. */}
          <Button onClick={sessionLapsed ? logout : cancelReauth} disabled={busy} color="inherit">
            {sessionLapsed ? t('session.signOut') : t('session.cancel')}
          </Button>
          {isSso ? (
            ssoExhausted || terminalRefusal ? (
              <Button onClick={handleSsoRedirect} variant="contained">
                {t('session.reauthSsoRedirect')}
              </Button>
            ) : (
              <Button onClick={handleSsoRetry} disabled={busy} variant="contained">
                {t('session.reauthRetry')}
              </Button>
            )
          ) : (
            <Button type="submit" disabled={busy || !password} variant="contained">
              {t('session.reauthSubmit')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReauthDialog;
